import {
  ResumeRalanBlanko,
  ResumeRalanPaketPayload,
  ResumeRalanPrefill
} from "@/types/resumeRalanBlanko";
import { SoapRanapResponseItem } from "@/types/soap";

const LABEL_KESADARAN: Record<string, string> = {
  cm: "Compos Mentis",
  compos_mentis: "Compos Mentis",
  apatis: "Apatis",
  delirium: "Delirium",
  somnolen: "Somnolen",
  sopor: "Sopor",
  "semi-coma": "Semi Coma",
  semi_coma: "Semi Coma",
  coma: "Coma",
  koma: "Coma"
};

export function labelKesadaran(kode: unknown): string {
  const teks = String(kode == null ? "" : kode).trim();
  if (!teks) return "";

  const kunci = teks.toLowerCase();

  return LABEL_KESADARAN[kunci] || teks;
}

export function kosong(nilai: unknown): boolean {
  if (nilai === null || nilai === undefined) return true;

  return String(nilai).trim() === "";
}

/** Tekanan darah disimpan sebagai satu kolom string; jangan hasilkan "120/". */
export function composeTd(sistole: unknown, diastole: unknown): string | null {
  if (kosong(sistole) || kosong(diastole)) return null;

  return `${String(sistole).trim()}/${String(diastole).trim()}`;
}

const KOLOM_PAKET: string[] = [
  "dpjp_id",
  "discharge_id",
  "rujukan_ke",
  "catatan"
];

export function blankoBaru(): ResumeRalanBlanko {
  return {
    dpjp_id: null,
    discharge_id: null,
    rujukan_ke: null,
    catatan: null,
    asal_rujukan_text: null,
    keluhan_utama: null,
    alergi: null,
    riwayat_penyakit_sekarang: null,
    riwayat_penyakit_dahulu: null,
    riwayat_penyakit_keluarga: null,
    pf_ku: null,
    pf_kesadaran: null,
    pf_td: null,
    pf_nadi: null,
    pf_nafas: null,
    pf_suhu: null,
    pf_tinggi_badan: null,
    pf_berat_badan: null,
    pf_lain: null,
    penunjang_lab: null,
    penunjang_radiologi: null,
    penunjang_lain: null,
    diagnosa_akhir: null,
    terapi_tindakan: null,
    tl_status: null,
    tl_prb: false,
    tl_iterasi: false,
    tl_rujuk_balik_fktp: false,
    tl_kontrol: false,
    tl_kontrol_note: null,
    tl_tgl_kontrol: null,
    tl_prb_note: null,
    tgl_surat: null
  };
}

const KOLOM_ISI: Array<keyof ResumeRalanBlanko> = [
  "keluhan_utama",
  "alergi",
  "riwayat_penyakit_sekarang",
  "riwayat_penyakit_dahulu",
  "riwayat_penyakit_keluarga",
  "pf_ku",
  "pf_kesadaran",
  "pf_td",
  "pf_nadi",
  "pf_nafas",
  "pf_suhu",
  "pf_tinggi_badan",
  "pf_berat_badan",
  "pf_lain",
  "penunjang_lab",
  "penunjang_radiologi",
  "penunjang_lain",
  "diagnosa_akhir",
  "terapi_tindakan"
];

export function blankoMasihKosong(form: ResumeRalanBlanko | null): boolean {
  if (!form) return true;

  const adaIsi = KOLOM_ISI.some(kolom => !kosong(form[kolom]));
  const adaTindakLanjut =
    !kosong(form.tl_status) ||
    form.tl_prb ||
    form.tl_iterasi ||
    form.tl_rujuk_balik_fktp ||
    form.tl_kontrol;

  return !adaIsi && !adaTindakLanjut;
}

export function blankoDariPayload(row: any): ResumeRalanBlanko {
  const form = blankoBaru();
  if (!row) return form;

  (Object.keys(form) as Array<keyof ResumeRalanBlanko>).forEach(kunci => {
    if (!(kunci in row)) return;

    const nilai = row[kunci];

    if (
      kunci === "tl_prb" ||
      kunci === "tl_iterasi" ||
      kunci === "tl_rujuk_balik_fktp" ||
      kunci === "tl_kontrol"
    ) {
      (form[kunci] as boolean) = nilai === true || nilai === 1 || nilai === "1";
      return;
    }

    (form[kunci] as any) = nilai === undefined ? null : nilai;
  });

  if (kosong(form.tl_status) && !kosong(row.tl_prb_id)) {
    const prb = String(row.tl_prb_id);
    if (prb === "1") form.tl_status = "stabil";
    if (prb === "2") form.tl_status = "tidak_stabil";
    if (prb === "3") {
      form.tl_status = "stabil";
      form.tl_iterasi = true;
    }
  }

  if (kosong(form.asal_rujukan_text) && !kosong(row.asal_rujukan_nama)) {
    form.asal_rujukan_text = row.asal_rujukan_nama;
  }

  if (kosong(form.dpjp_id) && row.dpjp) {
    form.dpjp_id = row.dpjp.id ?? null;
  }

  return form;
}

export function pisahPayload(
  form: ResumeRalanBlanko
): { paket: ResumeRalanPaketPayload; blanko: Partial<ResumeRalanBlanko> } {
  const paket: ResumeRalanPaketPayload = {
    dpjp_id: form.dpjp_id,
    discharge_id: form.discharge_id,
    rujukan_ke: form.rujukan_ke,
    catatan: form.catatan
  };

  const blanko: Partial<ResumeRalanBlanko> = {};

  (Object.keys(form) as Array<keyof ResumeRalanBlanko>).forEach(kunci => {
    if (KOLOM_PAKET.indexOf(kunci as string) !== -1) return;
    (blanko[kunci] as any) = form[kunci];
  });

  return { paket, blanko };
}


export function isiYangKosong(
  form: ResumeRalanBlanko,
  sumber: Partial<Record<keyof ResumeRalanBlanko, unknown>>
): Array<string> {
  const terisi: Array<string> = [];

  (Object.keys(sumber) as Array<keyof ResumeRalanBlanko>).forEach(kunci => {
    const nilai = sumber[kunci];
    if (kosong(nilai)) return;
    if (!kosong(form[kunci])) return;

    (form[kunci] as any) = nilai;
    terisi.push(kunci as string);
  });

  return terisi;
}

export function dariPrefill(
  fields: ResumeRalanPrefill | null
): Partial<Record<keyof ResumeRalanBlanko, unknown>> {
  if (!fields) return {};

  return {
    keluhan_utama: fields.keluhan_utama,
    alergi: fields.alergi,
    riwayat_penyakit_sekarang: fields.riwayat_penyakit_sekarang,
    riwayat_penyakit_dahulu: fields.riwayat_penyakit_dahulu,
    pf_ku: fields.pf_ku,
    pf_kesadaran: labelKesadaran(fields.pf_kesadaran) || null,
    pf_td: composeTd(fields.pf_sistole, fields.pf_diastole),
    pf_nadi: fields.pf_nadi,
    pf_nafas: fields.pf_nafas,
    pf_suhu: fields.pf_suhu,
    pf_tinggi_badan: fields.pf_tinggi_badan,
    pf_berat_badan: fields.pf_berat_badan
  };
}

export function dariSoap(
  soap: SoapRanapResponseItem | null | undefined
): Partial<Record<keyof ResumeRalanBlanko, unknown>> {
  if (!soap) return {};

  return {
    diagnosa_akhir: String(soap.assesment || "").trim(),
    terapi_tindakan: String(soap.plan || "").trim()
  };
}

export function ringkasIcd(rows: Array<any>): string | null {
  const baris = (rows || [])
    .slice()
    .sort((a, b) => Number(a && a.urut) - Number(b && b.urut))
    .map(row => {
      const kode = String((row && row.kode) || "").trim();
      const nama = String((row && (row.text || row.nama)) || "").trim();

      if (!kode && !nama) return "";

      return kode && nama ? `${kode} — ${nama}` : kode || nama;
    })
    .filter(teks => !!teks);

  return baris.length ? baris.join("\r\n") : null;
}

export default {
  labelKesadaran,
  kosong,
  composeTd,
  blankoBaru,
  blankoMasihKosong,
  blankoDariPayload,
  pisahPayload,
  isiYangKosong,
  dariPrefill,
  dariSoap,
  ringkasIcd
};
